import { animate, state, style, transition, trigger } from '@angular/animations';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DateHelper } from '@hub4b/helpers/date-helper';
import { DocumentType } from '@hub4b/models/enums/document-type';
import { FolderDocumentsRefreshEvent } from '@hub4b/models/events/folder-documents-refresh-event';
import { Folder } from '@hub4b/models/server/folder';
import { FolderDocument } from '@hub4b/models/server/folder-document';
import { AvmEmailAddressesPipe } from '@hub4b/pipes/avm-email-addresses.pipe';
import { FolderListEventsService } from '@hub4b/services/events/folder-list-events.service';
import { FolderDocumentsService } from '@hub4b/services/http/folder-documents.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-expanded-detail-cell',
    templateUrl: './expanded-detail-cell.component.html',
    styleUrls: ['./expanded-detail-cell.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('void', style({ height: '0px', minHeight: '0' })),
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('150ms')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIconModule, DatePipe, AvmEmailAddressesPipe]
})
export class ExpandedDetailCellComponent implements OnInit, OnDestroy {
    /**
     * Références des Enums utilisées dans le template.
     */
    public DocumentType = DocumentType;

    /**
     * Références des constantes utilisées dans le template.
     */
    public FRENCH_DATETIME_FORMAT = DateHelper.FRENCH_DATETIME_FORMAT;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Liste des documents affichés lorsque le dossier est déplié.
     */
    public documents: FolderDocument[] = [];

    /**
     * Instance du dossier affiché sur cette ligne.
     */
    @Input()
    row: Folder;

    /**
     * Valeur indiquant si la ligne est dépliée.
     */
    private _isExpanded: boolean = false;

    /**
     * Obtient une valeur indiquant si la ligne est dépliée.
     */
    get isExpanded(): boolean {
        return this._isExpanded;
    }

    /**
     * Spécifie une valeur indiquant si la ligne est dépliée.
     */
    @Input()
    set isExpanded(isExpanded: boolean) {
        this._isExpanded = isExpanded;
        if (isExpanded) {
            this.foldersDocumentsService
                .list(this.row.viewId, this.row.id)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((documents) => {
                    this.displayDocuments(documents);
                });
        }
    }

    /**
     * Constructeur.
     * @param foldersDocumentsService
     * @param cdRef
     */
    constructor(
        private foldersDocumentsService: FolderDocumentsService,
        private folderListEventsService: FolderListEventsService,
        protected cdRef: ChangeDetectorRef
    ) {}

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.subscribeToFolderDocumentsRefreshEvent();
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * S'abonne aux événements de mise à jour des documents d'un dossier.
     */
    private subscribeToFolderDocumentsRefreshEvent() {
        this.folderListEventsService.folderDocumentsUpdated.pipe(takeUntil(this.componentDestroyed$)).subscribe((event: FolderDocumentsRefreshEvent) => {
            if (this.isExpanded && this.row.id === event.folderId) {
                this.displayDocuments(event.documents);
            }
        });
    }

    /**
     * Affiche les documents
     * @param documents : Liste des documents à afficher.
     */
    private displayDocuments(documents: FolderDocument[]) {
        this.documents = documents.sort((x, y) => y.creationDate.getTime() - x.creationDate.getTime());
        this.cdRef.detectChanges();
    }
}
